/**
 * @fileoverview Calendar sync (Spec 19) on the web, shared by every portal:
 * which choices to offer, Google's sign-in window and what it hands back, and
 * the words for each result.
 *
 * Google: the click opens a window at once (a window opened after a network
 * call is a blocked popup), the server's sign-in URL is loaded into it, and
 * Google returns to Hans's API, whose page posts `{ref}` (or `{error}`) back to
 * this window and closes. The portal's own session then trades the `ref` in
 * `finish`. When the browser blocked the window the page goes to Google itself
 * and comes back to the portal's page with `?calendar_sync=google&ref=…`.
 */

import { formatDistanceStrict } from "date-fns";

import BACKEND_URL from "./backendUrl";

/** The three choices, by id, in the server's default order. */
export const CHOICE_IDS = ["google", "apple", "other"];

/** Fired after any change, so every view of the same portal reloads. */
export const CALENDAR_SYNC_CHANGED = "hans:calendar-sync-changed";

export const announceChange = () => window.dispatchEvent(new Event(CALENDAR_SYNC_CHANGED));

/** What one line of each portal's chooser says the calendar holds. */
export const SYNC_DESCRIPTIONS = {
  instructor: "Every lesson and class you teach, kept up to date as your schedule changes. Students appear by first name.",
  student: "Your lessons and classes with every teacher, kept up to date when a time changes or a class is cancelled.",
  parent: "Every lesson and class for each of your children, kept up to date.",
  org: "Every class meeting across your programs, with its room and who teaches it.",
  viewer: "This program’s class meetings, kept up to date.",
};

/** A Google sign-in that did not connect, by the reason the server gave. */
export const GOOGLE_ERRORS = {
  denied: "Google Calendar wasn’t connected.",
  scope: "Hans needs permission to add its calendar. Try again and allow it.",
  expired: "That Google sign-in expired. Try again.",
  unavailable: "Google Calendar isn’t available right now. Try again later.",
  failed: "Google Calendar didn’t connect. Try again.",
};

/** Is anything syncing? A Google calendar that needs reconnecting is not. */
export function isSynced(sync) {
  return Boolean(sync && (sync.feed || sync.google?.status === "active"));
}

/**
 * The choices to show, in the server's order (Apple first for an account
 * that signs in with Apple): switched on here and not already in use. The
 * private link is one thing, so making it uses up both Apple and Other.
 */
export function choicesFor(sync, available) {
  const order = Array.isArray(sync?.order) && sync.order.length ? sync.order : CHOICE_IDS;
  return order.filter((id) => {
    if (id === "google") return Boolean(available?.google) && !sync?.google;
    if (id === "apple" || id === "other") return Boolean(available?.link) && !sync?.feed;
    return false;
  });
}

/** "Last synced 2 minutes ago", or that the first sync is still running. */
export function lastSynced(iso, now = new Date()) {
  const at = iso ? new Date(iso) : null;
  if (!at || Number.isNaN(at.getTime())) return "First sync in progress";
  if (now - at < 60_000) return "Last synced just now";
  return `Last synced ${formatDistanceStrict(at, now, { addSuffix: true })}`;
}

/** "Created Oct 12, 2026", from the link's latest reset. */
export function linkCreated(feed) {
  const at = new Date(feed?.rotated_at || feed?.created_at || "");
  if (Number.isNaN(at.getTime())) return null;
  return `Created ${at.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
}

/** The businesses Scheduler already sends to Google, as one sentence, or null. */
export function schedulerSkipLine(skips) {
  const names = (skips || []).filter(Boolean);
  if (!names.length) return null;
  const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
  return `Bookings from ${list} already reach Google Calendar through Kalver Scheduler, so they’re left out here.`;
}

/** The origin Google's return page posts from: the API's. */
export function apiOrigin() {
  try {
    return new URL(BACKEND_URL || window.location.origin, window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
}

/** Open Google's window now, while the click still allows it. Null when blocked. */
export function openSignInWindow() {
  const width = 520;
  const height = 680;
  const left = Math.round((window.screenX || 0) + Math.max(0, ((window.outerWidth || width) - width) / 2));
  const top = Math.round((window.screenY || 0) + Math.max(0, ((window.outerHeight || height) - height) / 3));
  try {
    return window.open("", "hans-google-calendar", `popup,width=${width},height=${height},left=${left},top=${top}`) || null;
  } catch {
    return null;
  }
}

/**
 * Wait for Google's window to hand back `{ref}` or `{error}`, or `{closed}`
 * when the person shut it. Only a message from that window, on the API's
 * origin, counts. A window that closes is given a moment, because its last
 * message can arrive just after it has gone.
 */
export function awaitSignIn(popup, { origin = apiOrigin(), grace = 1000, poll = 400 } = {}) {
  return new Promise((resolve) => {
    let closedAt = null;
    let timer = null;
    const done = (value) => {
      window.removeEventListener("message", onMessage);
      clearInterval(timer);
      resolve(value);
    };
    function onMessage(event) {
      if (event.source !== popup || event.origin !== origin) return;
      const data = event.data;
      if (!data || data.type !== "hans:calendar-sync" || data.provider !== "google") return;
      done(data.ref ? { ref: String(data.ref) } : { error: String(data.error || "failed") });
    }
    window.addEventListener("message", onMessage);
    timer = setInterval(() => {
      if (!popup.closed) return;
      closedAt ??= Date.now();
      if (Date.now() - closedAt >= grace) done({ closed: true });
    }, poll);
  });
}

/**
 * The result a full-page return carries (a blocked window went to Google
 * itself), and the query to leave behind once it is read; null otherwise.
 */
export function googleReturn(search) {
  const params = new URLSearchParams(search || "");
  if (params.get("calendar_sync") !== "google") return null;
  const ref = params.get("ref");
  const error = params.get("error");
  if (!ref && !error) return null;
  ["calendar_sync", "ref", "error"].forEach((key) => params.delete(key));
  const rest = params.toString();
  return { ref, error, search: rest ? `?${rest}` : "" };
}
